import React from 'react';
import { motion } from 'framer-motion';
import { X, FileText, MapPin, Building2, Download, Printer, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { formatPrice } from '../lib/utils';
import QuoteOrderProgress from './QuoteOrderProgress';
import { groupCartByRoom } from '../lib/cartGroups';
import { taxLabel } from './tax/taxDisplay';

// Company Information
const COMPANY_INFO = {
  name: 'Apollo Shades',
  address: '1 Brooklyn Navy Yard, Building 33, Suite 201B',
  city: 'Brooklyn',
  state: 'NY',
  zip: '11205',
  phone: '(212) 209-0654',
  email: 'info@apolloshades.com',
  website: 'www.apolloshades.com'
};

// Helper function to format address (handles both string and object formats)
const formatAddress = (address) => {
  if (!address) return null;
  if (typeof address === 'string') return address;
  if (typeof address === 'object') {
    return address.street || '';
  }
  return '';
};

const getAddressCityStateZip = (quote, prefix) => {
  // Check if address is an object with city/state/zip
  const address = quote[`${prefix}_address`];
  if (address && typeof address === 'object') {
    return [address.city, address.state, address.zip].filter(Boolean).join(', ');
  }
  // Fall back to separate fields
  return [quote[`${prefix}_city`], quote[`${prefix}_state`], quote[`${prefix}_zip`]].filter(Boolean).join(', ');
};

export const QuotePreviewModal = ({ quote, onClose, onDownload, onPrint, headerBadge, extra }) => {
  if (!quote) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-amber-600" />
            <h2 className="font-semibold">Quote #{quote.id?.slice(0, 8).toUpperCase()}</h2>
            {headerBadge}
          </div>
          <div className="flex items-center gap-2">
            {onPrint && (
              <Button onClick={onPrint} variant="outline" size="sm">
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
            )}
            {onDownload && (
              <Button onClick={onDownload} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {extra}

        {/* Quote Content */}
        <div className="p-6">
          {/* Company Header */}
          <div className="bg-neutral-900 text-white p-6 rounded-t-lg">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-serif">{COMPANY_INFO.name}</h2>
                <p className="text-neutral-400 mt-1">Custom Window Treatments</p>
              </div>
              <div className="text-right text-sm">
                <p>{COMPANY_INFO.address}</p>
                <p>{COMPANY_INFO.city}, {COMPANY_INFO.state} {COMPANY_INFO.zip}</p>
                <p className="mt-2">{COMPANY_INFO.phone}</p>
                <p>{COMPANY_INFO.email}</p>
              </div>
            </div>
          </div>

          {/* Quote Info Bar */}
          <div className="bg-amber-50 border-x border-amber-200 px-6 py-3 flex justify-between text-sm">
            <span><strong>Quote #:</strong> {quote.id?.slice(0, 8).toUpperCase()}</span>
            <span><strong>Date:</strong> {new Date(quote.created_at).toLocaleDateString()}</span>
            <span><strong>Status:</strong> <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              quote.status === 'approved' ? 'bg-green-100 text-green-700' :
              quote.status === 'pending' ? 'bg-amber-100 text-amber-700' :
              'bg-neutral-100 text-neutral-700'
            }`}>{quote.status?.charAt(0).toUpperCase() + quote.status?.slice(1)}</span></span>
          </div>

          {/* Addresses Section */}
          <div className="grid md:grid-cols-2 gap-6 p-6 border-x border-neutral-200 bg-white">
            {/* Bill To */}
            <div>
              <h3 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Bill To
              </h3>
              <div className="bg-neutral-50 p-4 rounded-lg">
                <p className="font-medium">{quote.customer_name}</p>
                {quote.company_name && <p className="text-neutral-600">{quote.company_name}</p>}
                {(quote.billing_address || quote.billing_city) && (
                  <>
                    <p className="text-neutral-600 mt-2">{formatAddress(quote.billing_address)}</p>
                    <p className="text-neutral-600">
                      {getAddressCityStateZip(quote, 'billing')}
                    </p>
                  </>
                )}
                <p className="text-neutral-600 mt-2">{quote.customer_email}</p>
                {quote.customer_phone && <p className="text-neutral-600">{quote.customer_phone}</p>}
              </div>
            </div>

            {/* Ship To / Jobsite */}
            <div>
              <h3 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Ship To / Jobsite
              </h3>
              <div className="bg-neutral-50 p-4 rounded-lg">
                {quote.same_as_billing ? (
                  <p className="text-neutral-500 italic">Same as billing address</p>
                ) : (
                  <>
                    <p className="font-medium">{quote.customer_name}</p>
                    {(quote.shipping_address || quote.shipping_city) && (
                      <>
                        <p className="text-neutral-600 mt-2">{formatAddress(quote.shipping_address)}</p>
                        <p className="text-neutral-600">
                          {getAddressCityStateZip(quote, 'shipping')}
                        </p>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Order Progress Section */}
          <div className="p-6 border-x border-neutral-200 bg-white">
            <QuoteOrderProgress 
              quote={quote}
              compact={false}
              expandable={false}
            />
          </div>

          {/* Line Items */}
          <div className="p-6 border border-neutral-200 bg-white rounded-b-lg">
            <h3 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-4">Items</h3>
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-xs font-medium text-neutral-500 uppercase">
                  <th className="pb-3">Description</th>
                  <th className="pb-3 text-center">Size</th>
                  <th className="pb-3 text-center">Qty</th>
                  <th className="pb-3 text-right">Price</th>
                </tr>
              </thead>
              <tbody>
                {groupCartByRoom(quote.items).map((group) => (
                  <React.Fragment key={group.room}>
                    <tr className="bg-stone-50" data-testid={`quote-room-group-${group.room.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                      <td className="py-2 font-semibold" colSpan={3}>{group.room} <span className="text-xs font-normal text-neutral-400">· {group.quantity} {group.quantity === 1 ? 'shade' : 'shades'}</span></td>
                      <td className="py-2 text-right font-mono font-semibold" data-testid="quote-room-subtotal">{formatPrice(group.subtotal)}</td>
                    </tr>
                    {group.items.map((item, idx) => (
                      <tr key={item.id || idx} className="border-b border-neutral-100">
                        <td className="py-3 pl-4">
                          <p className="font-medium">{item.windowLabel || item.config?.product_type?.replace('-', ' ')}</p>
                          <p className="text-sm text-neutral-500">
                            {item.windowLabel ? `${item.config?.product_type?.replace('-', ' ')} · ` : ''}{item.config?.fabric_material} - {item.config?.fabric_color}
                            {item.config?.control_type === 'motorized' && ' (Motorized)'}
                            {item.config?.needs_installation && ' + Installation'}
                          </p>
                        </td>
                        <td className="py-3 text-center text-sm">{item.config?.width}" × {item.config?.height}"</td>
                        <td className="py-3 text-center">{item.quantity}</td>
                        <td className="py-3 text-right font-mono">{formatPrice(item.config?.total_price)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="mt-6 flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500">Subtotal</span>
                  <span className="font-mono">{formatPrice(quote.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500" data-testid="preview-tax-label">{taxLabel(quote)}</span>
                  <span className="font-mono">{quote.tax_details?.status === 'pending_address' ? 'TBC' : formatPrice(quote.tax)}</span>
                </div>
                <div className="flex justify-between text-lg font-medium pt-2 border-t border-neutral-200">
                  <span>Total</span>
                  <span className="font-mono">{formatPrice(quote.total)}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {quote.notes && (
              <div className="mt-6 p-4 bg-neutral-50 rounded-lg">
                <h4 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">Notes</h4>
                <p className="text-sm text-neutral-600">{quote.notes}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-4 p-4 bg-neutral-50 rounded-lg text-center">
            <p className="text-sm text-neutral-600">
              Thank you for choosing {COMPANY_INFO.name}! Quote valid for 30 days. 
              Contact us at {COMPANY_INFO.phone} or {COMPANY_INFO.email} with any questions.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default QuotePreviewModal;
